import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { scheduleIdle } from "@/lib/scheduleIdle";
import {
  prefetchProductionPage,
  prefetchSiblingProductionPages,
  productionPageFromPath,
  type ProductionPage,
} from "@/lib/prefetch/productionPagePrefetch";

/**
 * Productions whose sibling pages have already been warmed this session.
 *
 * Module-level on purpose. Every production page renders its own sidebar, so
 * this hook unmounts and remounts on every navigation between them — a
 * component-scoped flag would re-schedule the whole sweep on each hop, and the
 * unmount would cancel the pending one before it ever fired. Which is to say:
 * the producer clicking straight through the sidebar, the exact person the
 * idle sweep is for, would never have gotten it.
 */
const sweptProductions = new Set<string>();

/**
 * Bumped whenever the signed-in user changes. A sweep captures the value it
 * started under and stops the moment it no longer matches.
 *
 * Without this the sweep would keep reading after sign-out. Those reads do not
 * fail — RLS answers a signed-out client with an empty set — so React Query
 * would cache `[]` as a fresh success for every page, and the next producer to
 * sign in on that browser would see an empty crew roster and an empty schedule
 * for five minutes, with no error and no spinner.
 */
let authGeneration = 0;

/**
 * Stop any in-flight warm and forget what has been warmed.
 *
 * Called from AuthContext alongside the query-cache clear on sign-out — the
 * cache clear alone only stops what is already in flight.
 */
export function resetProductionPrefetchState(): void {
  authGeneration += 1;
  sweptProductions.clear();
}

/**
 * Keep the other pages of an open production warm.
 *
 * Two moments, both aimed at the same complaint — pages that only start
 * working once you click them:
 *
 * - **On hover / keyboard focus of a nav item**, warm that page. The few
 *   hundred milliseconds between pointing at a link and clicking it are enough
 *   to hide most of the fetch.
 * - **When the browser first goes idle inside a production**, warm every
 *   sibling page once, so the producer who clicks straight through the sidebar
 *   without pausing still lands on a warm page.
 *
 * Mounted from `ProductionSidebar`, which every production page renders.
 */
export function useProductionPagePrefetch(productionId: string | undefined): {
  prefetchPage: (page: ProductionPage | null) => void;
} {
  const queryClient = useQueryClient();
  const { userId } = useAuth();
  const location = useLocation();

  // Read at warm time, not baked into the effect's dependencies — the sweep
  // runs once per production, not once per page visited.
  const currentPage = productionId ? productionPageFromPath(location.pathname, productionId) : null;
  const currentPageRef = useRef<ProductionPage | null>(currentPage);
  currentPageRef.current = currentPage;

  const prefetchPage = useCallback(
    (page: ProductionPage | null) => {
      if (!productionId || !page) return;
      void prefetchProductionPage(queryClient, page, {
        productionId,
        userId: userId ?? undefined,
      });
    },
    [queryClient, productionId, userId],
  );

  useEffect(() => {
    if (!productionId || sweptProductions.has(productionId)) return;
    sweptProductions.add(productionId);

    // Not cancelled on unmount: the point of the sweep is the pages the
    // producer is about to open, so a navigation away from this sidebar is
    // exactly when its results start paying off. It IS abandoned when the
    // signed-in user changes — see authGeneration.
    const startedUnder = authGeneration;
    scheduleIdle(() => {
      if (startedUnder !== authGeneration) return;
      void prefetchSiblingProductionPages(
        queryClient,
        currentPageRef.current,
        { productionId, userId: userId ?? undefined },
        undefined,
        () => startedUnder !== authGeneration,
      );
    });
  }, [queryClient, productionId, userId]);

  return { prefetchPage };
}
